import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Search,
  Loader2,
  ClipboardList,
  StickyNote,
  ChevronLeft,
  ChevronRight,
} from 'lucide-react';
import { parseISOToLocalDate } from '../utils/dateUtils';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  Input,
  Button,
  Badge,
  Select,
  SelectTrigger,
  SelectContent,
  SelectItem,
  SelectValue,
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@roger/ui-components';
import { useStore } from '../store';
import { useShallow } from 'zustand/react/shallow';
import { getMyTasksTasksMyTasksGet } from '../services/api/sdk.gen';
import { client } from '../services/api';
import type { HotboxTaskResponse } from '../services/api/types.gen';

type RoleFilter = 'all' | 'clinician' | 'case_manager';

const ALL_CLINICS = '__all__';
const PAGE_SIZE = 50;

const STATUS_BADGE_VARIANT: Record<
  string,
  'default' | 'secondary' | 'destructive' | 'outline'
> = {
  not_started: 'outline',
  saved: 'secondary',
  submitted: 'default',
  submitted_with_signature: 'default',
  pending_clinician_signature: 'destructive',
  completed: 'secondary',
};

const STATUS_LABEL: Record<string, string> = {
  not_started: 'Not Started',
  saved: 'Saved',
  submitted: 'Submitted',
  submitted_with_signature: 'Submitted (Signed)',
  pending_clinician_signature: 'Pending Signature',
  completed: 'Completed',
  cancelled: 'Cancelled',
  pending: 'Pending',
  scheduled: 'Scheduled',
  in_progress: 'In Progress',
};

function formatDate(dateStr: string): string {
  const d = parseISOToLocalDate(dateStr);
  if (!d) return '—';
  return d.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

function formatVisitType(visitType: string): string {
  return visitType
    .replace(/_/g, ' ')
    .replace(/\b\w/g, c => c.toUpperCase())
    .replace(/\bOasis\b/i, 'OASIS')
    .replace(/\bSoc\b/i, 'SOC')
    .replace(/\bRoc\b/i, 'ROC')
    .replace(/\bRn\b/i, 'RN')
    .replace(/\bLpn\b/i, 'LPN')
    .replace(/\bPt\b/i, 'PT')
    .replace(/\bOt\b/i, 'OT')
    .replace(/\bSt\b/i, 'ST')
    .replace(/\bMsw\b/i, 'MSW')
    .replace(/\bHha\b/i, 'HHA');
}

const Hotbox: React.FC = () => {
  const navigate = useNavigate();
  const { showError, user, agencyClinics, fetchAgencyClinics, hasPermission } =
    useStore(
      useShallow(state => ({
        showError: state.showError,
        user: state.user,
        agencyClinics: state.agencyClinics,
        fetchAgencyClinics: state.fetchAgencyClinics,
        hasPermission: state.hasPermission,
      }))
    );

  useEffect(() => {
    if (!hasPermission('emr:manage') && !hasPermission('hotbox:access')) {
      navigate('/dashboard');
    }
  }, [hasPermission, navigate]);

  const userClinics = user?.user_clinics ?? [];

  const clinicOptions = useMemo(() => {
    return userClinics
      .map(uc => {
        const clinic = agencyClinics.find(ac => ac.id === uc.clinic_id);
        return clinic
          ? { id: clinic.id, name: clinic.name, userClinic: uc }
          : null;
      })
      .filter((c): c is NonNullable<typeof c> => c !== null)
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [userClinics, agencyClinics]);

  const [selectedClinicId, setSelectedClinicId] = useState<string>(ALL_CLINICS);
  const [tasks, setTasks] = useState<HotboxTaskResponse[]>([]);
  const [loading, setLoading] = useState(true);
  const [roleFilter, setRoleFilter] = useState<RoleFilter>('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [page, setPage] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const fetchIdRef = useRef(0);

  useEffect(() => {
    fetchAgencyClinics(user?.agency?.id);
  }, [fetchAgencyClinics, user?.agency?.id]);

  const isAllClinics = selectedClinicId === ALL_CLINICS;

  const selectedUserClinic = useMemo(
    () =>
      isAllClinics
        ? undefined
        : clinicOptions.find(c => c.id === selectedClinicId)?.userClinic,
    [clinicOptions, selectedClinicId, isAllClinics]
  );

  const aggregateRoles = useMemo(() => {
    return {
      isClinician: userClinics.some(uc => uc.is_assignable_clinician),
      isCaseManager: userClinics.some(uc => uc.is_case_manager),
    };
  }, [userClinics]);

  const filterTabs = useMemo(() => {
    const tabs: { label: string; value: RoleFilter }[] = [];

    const isClinician = isAllClinics
      ? aggregateRoles.isClinician
      : (selectedUserClinic?.is_assignable_clinician ?? false);
    const isCaseManager = isAllClinics
      ? aggregateRoles.isCaseManager
      : (selectedUserClinic?.is_case_manager ?? false);

    if (isClinician && isCaseManager) {
      tabs.push({ label: 'All Tasks', value: 'all' });
      tabs.push({ label: 'Clinician', value: 'clinician' });
      tabs.push({ label: 'Case Manager', value: 'case_manager' });
    } else if (isClinician) {
      tabs.push({ label: 'Clinician', value: 'clinician' });
    } else if (isCaseManager) {
      tabs.push({ label: 'Case Manager', value: 'case_manager' });
    }

    return tabs;
  }, [isAllClinics, aggregateRoles, selectedUserClinic]);

  const hasAnyRole = aggregateRoles.isClinician || aggregateRoles.isCaseManager;

  useEffect(() => {
    if (
      filterTabs.length > 0 &&
      !filterTabs.some(t => t.value === roleFilter)
    ) {
      setRoleFilter(filterTabs[0].value);
    }
  }, [filterTabs, roleFilter]);

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(searchTerm), 300);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  useEffect(() => {
    setPage(0);
  }, [roleFilter, debouncedSearch, selectedClinicId]);

  useEffect(() => {
    const id = ++fetchIdRef.current;
    setLoading(true);

    getMyTasksTasksMyTasksGet({
      client,
      query: {
        role_filter: roleFilter,
        search: debouncedSearch || '',
        clinic_id: isAllClinics ? undefined : selectedClinicId || undefined,
        limit: PAGE_SIZE + 1,
        offset: page * PAGE_SIZE,
      },
    })
      .then(response => {
        if (id !== fetchIdRef.current) return;
        const data = Array.isArray(response.data) ? response.data : [];
        setHasMore(data.length > PAGE_SIZE);
        setTasks(data.slice(0, PAGE_SIZE));
      })
      .catch(() => {
        if (id !== fetchIdRef.current) return;
        setTasks([]);
        showError('Failed to load tasks');
      })
      .finally(() => {
        if (id === fetchIdRef.current) {
          setLoading(false);
        }
      });
  }, [
    roleFilter,
    debouncedSearch,
    selectedClinicId,
    isAllClinics,
    page,
    showError,
  ]);

  const handleRowClick = (task: HotboxTaskResponse, e: React.MouseEvent) => {
    const path = `/patients/${task.patient_id}/visits/${task.id}`;
    if (e.metaKey || e.ctrlKey) {
      window.open(path, '_blank');
    } else {
      navigate(path);
    }
  };

  const handleClinicChange = (clinicId: string) => {
    setSelectedClinicId(clinicId);
    setSearchTerm('');
  };

  if (!hasAnyRole) {
    return (
      <div className='space-y-4'>
        <div className='flex items-center gap-2'>
          <ClipboardList className='h-6 w-6 text-primary' />
          <h1 className='text-2xl font-bold'>Hotbox</h1>
        </div>
        <div className='rounded-md border p-12 text-center text-muted-foreground'>
          <ClipboardList className='mx-auto mb-3 h-10 w-10 text-muted-foreground/50' />
          <p className='text-lg font-medium'>No tasks to show</p>
          <p className='mt-1 text-sm'>
            The Hotbox displays tasks for clinicians and case managers. You are
            not currently assigned either role at any clinic.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className='space-y-4'>
      <div className='flex items-center justify-between'>
        <div className='flex items-center gap-2'>
          <ClipboardList className='h-6 w-6 text-primary' />
          <h1 className='text-2xl font-bold'>Hotbox</h1>
        </div>
      </div>

      <div className='flex flex-wrap items-center gap-3'>
        <Select value={selectedClinicId} onValueChange={handleClinicChange}>
          <SelectTrigger selectSize='sm' className='w-56'>
            <SelectValue placeholder='Select clinic...' />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={ALL_CLINICS}>All Clinics</SelectItem>
            {clinicOptions.map(clinic => (
              <SelectItem key={clinic.id} value={clinic.id}>
                {clinic.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        <div className='relative'>
          <Search className='absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground' />
          <Input
            placeholder='Search by patient name...'
            value={searchTerm}
            onChange={e => setSearchTerm(e.target.value)}
            className='pl-9 w-72'
            inputSize='sm'
          />
        </div>

        {filterTabs.length > 1 && (
          <div className='flex rounded-md border border-border overflow-hidden'>
            {filterTabs.map(tab => (
              <button
                key={tab.value}
                onClick={() => setRoleFilter(tab.value)}
                className={`px-3 py-1.5 text-sm font-medium transition-colors ${
                  roleFilter === tab.value
                    ? 'bg-primary text-primary-foreground'
                    : 'bg-background hover:bg-accent text-foreground'
                }`}
              >
                {tab.label}
              </button>
            ))}
          </div>
        )}

        {loading && (
          <Loader2 className='h-4 w-4 animate-spin text-muted-foreground' />
        )}
      </div>

      <div className='rounded-md border'>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className='w-28'>Date</TableHead>
              <TableHead className='w-48'>Patient</TableHead>
              {isAllClinics && <TableHead className='w-40'>Clinic</TableHead>}
              <TableHead className='w-40'>Type</TableHead>
              <TableHead className='w-40'>Status</TableHead>
              <TableHead>
                <div className='flex items-center gap-1'>
                  <StickyNote className='h-3.5 w-3.5' />
                  Notes
                </div>
              </TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading && tasks.length === 0 ? (
              <TableRow>
                <TableCell
                  colSpan={isAllClinics ? 6 : 5}
                  className='h-32 text-center'
                >
                  <Loader2 className='mx-auto h-6 w-6 animate-spin text-muted-foreground' />
                </TableCell>
              </TableRow>
            ) : !loading && tasks.length === 0 ? (
              <TableRow>
                <TableCell
                  colSpan={isAllClinics ? 6 : 5}
                  className='h-32 text-center text-muted-foreground'
                >
                  {debouncedSearch
                    ? 'No tasks match your search.'
                    : 'No tasks found. Tasks will appear here when you are assigned as clinician or case manager.'}
                </TableCell>
              </TableRow>
            ) : (
              tasks.map(task => (
                <TableRow
                  key={task.id}
                  className='cursor-pointer hover:bg-accent/50 transition-colors'
                  onClick={e => handleRowClick(task, e)}
                >
                  <TableCell className='font-medium text-sm'>
                    {formatDate(task.visit_date)}
                  </TableCell>
                  <TableCell className='font-medium text-sm'>
                    {task.patient_name}
                  </TableCell>
                  {isAllClinics && (
                    <TableCell className='text-sm'>
                      {task.clinic_name || '—'}
                    </TableCell>
                  )}
                  <TableCell className='text-sm'>
                    {formatVisitType(task.visit_type)}
                  </TableCell>
                  <TableCell>
                    <Badge
                      variant={STATUS_BADGE_VARIANT[task.status] ?? 'outline'}
                    >
                      {STATUS_LABEL[task.status] ?? task.status}
                    </Badge>
                  </TableCell>
                  <TableCell className='text-sm text-muted-foreground max-w-xs truncate'>
                    {task.notes ? (
                      <TooltipProvider delayDuration={300}>
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <span className='truncate block'>{task.notes}</span>
                          </TooltipTrigger>
                          <TooltipContent
                            side='top'
                            className='max-w-sm whitespace-pre-line'
                          >
                            {task.notes}
                          </TooltipContent>
                        </Tooltip>
                      </TooltipProvider>
                    ) : (
                      '—'
                    )}
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>

      <div className='flex items-center justify-between text-sm text-muted-foreground'>
        <span>
          {tasks.length > 0
            ? `Showing ${page * PAGE_SIZE + 1}–${page * PAGE_SIZE + tasks.length}`
            : 'No results'}
        </span>
        <div className='flex items-center gap-2'>
          <Button
            variant='outline'
            size='sm'
            onClick={() => setPage(p => Math.max(0, p - 1))}
            disabled={page === 0}
          >
            <ChevronLeft className='h-4 w-4' />
            Previous
          </Button>
          <Button
            variant='outline'
            size='sm'
            onClick={() => setPage(p => p + 1)}
            disabled={!hasMore}
          >
            Next
            <ChevronRight className='h-4 w-4' />
          </Button>
        </div>
      </div>
    </div>
  );
};

export default Hotbox;
